/**
 * Incoming-call watcher.
 *
 * Only the signalling state lives here. The WebRTC peer connection itself is
 * not built yet - see the TURN warning in services/contracts/calls.js before
 * implementing it.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { calls } from '@zivapp/shared'
import { useAuth } from './AuthContext.jsx'

const CallContext = createContext(null)

export function CallProvider({ children }) {
  const { isSignedIn } = useAuth()
  const [incoming, setIncoming] = useState(null)
  const [active, setActive] = useState(null)

  useEffect(() => {
    if (!isSignedIn) return undefined
    return calls.subscribeToIncoming(setIncoming)
  }, [isSignedIn])

  const value = useMemo(() => ({
    incoming,
    active,
    async startCall(options) {
      const call = await calls.start(options)
      setActive(call)
      return call
    },
    async acceptCall(callId) {
      await calls.accept(callId)
      setActive(incoming)
      setIncoming(null)
    },
    async declineCall(callId) {
      await calls.decline(callId)
      setIncoming(null)
    },
    async endCall(callId) {
      await calls.end(callId)
      setActive(null)
    }
  }), [incoming, active])

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>
}

export function useCalls() {
  const context = useContext(CallContext)
  if (!context) throw new Error('useCalls must be used inside CallProvider')
  return context
}
